import { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectConversations,
  selectMessages,
  selectMessagesLoading,
  addMessage,
  fetchUnreadNotificationCount,
  selectNotificationUnreadCount
} from '../../../slices/messageSlice';
import {
  selectIsConnected,
  selectRecentMessage,
  setUserId,
  addMessage as addSSEMessage
} from '../../../slices/sseSlice';
import { config } from '../../../constants/config';

/**
 * Business Message Type Definitions
 * Maps message types to user roles and priorities
 */
const BUSINESS_MESSAGE_TYPES = {
  'VOICE_ACTOR_APPLICATION': {
    priority: 'HIGH',
    targetRoles: ['BOOK_CREATOR', 'ADMIN'],
    senderRoles: ['VOICE_ACTOR', 'BOOK_CREATOR'], // ← Add BOOK_CREATOR
    icon: 'user plus',
    color: '#2185d0'
  },
  'APPLICATION_STATUS_UPDATE': {
    priority: 'HIGH',
    targetRoles: ['VOICE_ACTOR'],
    senderRoles: ['BOOK_CREATOR', 'ADMIN'],
    icon: 'check circle',
    color: '#21ba45'
  },
  'PROJECT_MILESTONE': {
    priority: 'MEDIUM',
    targetRoles: ['BOOK_CREATOR', 'VOICE_ACTOR'],
    senderRoles: ['BOOK_CREATOR', 'VOICE_ACTOR', 'ADMIN'],
    icon: 'trophy',
    color: '#f2711c'
  },
  'FINANCIAL_TRANSACTION': {
    priority: 'CRITICAL',
    targetRoles: ['BOOK_CREATOR', 'VOICE_ACTOR'],
    senderRoles: ['BOOK_CREATOR', 'VOICE_ACTOR', 'ADMIN'],
    icon: 'dollar sign',
    color: '#db2828'
  },
  'SYSTEM_NOTIFICATION': {
    priority: 'LOW',
    targetRoles: ['ALL'],
    senderRoles: ['ADMIN', 'SYSTEM'],
    icon: 'info circle',
    color: '#767676'
  },
  'HELP_DESK': {
    priority: 'MEDIUM',
    targetRoles: ['ADMIN', 'SUPPORT'],
    senderRoles: ['GUEST', 'VOICE_ACTOR', 'BOOK_CREATOR'],
    icon: 'help circle',
    color: '#6435c9'
  }
};

/**
 * Custom hook for universal messaging functionality
 * Handles SSE connections, message filtering, and business logic
 */
export const useUniversalMessaging = (userId, userRole) => {
  const dispatch = useDispatch();
  const conversations = useSelector(selectConversations);
  const loading = useSelector(selectMessagesLoading);
  const isConnected = useSelector(selectIsConnected);
  const recentMessage = useSelector(selectRecentMessage);
  const notificationUnreadCount = useSelector(selectNotificationUnreadCount);

  // Local state
  const [connectionStatus, setConnectionStatus] = useState('Disconnected');
  const [sseEventSource, setSseEventSource] = useState(null);
  const [hasNewMessages, setHasNewMessages] = useState(false);

  // Determine user role from various sources
  const effectiveUserRole = useMemo(() => {
    if (userRole) return userRole;
    if (!userId) return 'guest';

    // Try to determine role from user data or default to 'user'
    // This could be enhanced to pull from user profile
    return 'user';
  }, [userRole, userId]);

  // Filter messages based on user role and business logic
  const filteredMessages = useMemo(() => {
    if (!conversations || conversations.length === 0) return [];

    const allMessages = conversations.flatMap(conv =>
      conv.messages || []
    );

    return allMessages.filter(message => {
      // Always show messages where user is sender or recipient
      if (message.senderId === userId || message.recipientId === userId) {
        return true;
      }

      // Filter business messages based on role
      if (message.messageType && BUSINESS_MESSAGE_TYPES[message.messageType]) {
        const messageConfig = BUSINESS_MESSAGE_TYPES[message.messageType];

        // Check if user role is in target roles
        if (messageConfig.targetRoles.includes('ALL')) return true;
        if (messageConfig.targetRoles.includes(effectiveUserRole.toUpperCase())) return true;

        // Special handling for admin role
        if (effectiveUserRole === 'admin') return true;
      }

      return false;
    });
  }, [conversations, userId, effectiveUserRole]);

  // Calculate unread counts
  const unreadCounts = useMemo(() => {
    const total = filteredMessages.filter(msg => !msg.isRead && msg.recipientId === userId).length;

    const business = filteredMessages.filter(msg =>
      !msg.isRead &&
      msg.recipientId === userId &&
      msg.messageType &&
      BUSINESS_MESSAGE_TYPES[msg.messageType]
    ).length;

    return { total, business };
  }, [filteredMessages, userId]);

  // Get business message types for current user
  const businessMessageTypes = useMemo(() => {
    const unreadBusinessMessages = filteredMessages.filter(msg =>
      !msg.isRead &&
      msg.recipientId === userId &&
      msg.messageType &&
      BUSINESS_MESSAGE_TYPES[msg.messageType]
    );

    const types = [...new Set(unreadBusinessMessages.map(msg => msg.messageType))];
    return types.map(type => type.replace('_', ' ').toLowerCase());
  }, [filteredMessages, userId]);

  // SSE Connection Management
  useEffect(() => {
    if (!userId) {
      setConnectionStatus('Not authenticated');
      return;
    }

    // Set user ID in SSE slice
    dispatch(setUserId(userId));

    // Fetch notification unread count on mount
    dispatch(fetchUnreadNotificationCount(userId));

    // Create SSE connection
    const connectSSE = () => {
      try {
        const sseUrl = `${config.API_SSE_ENDPOINT}?userId=${userId}`;
        const eventSource = new EventSource(sseUrl);

        eventSource.onopen = () => {
          console.log(`SSE connected for user ${userId}`);
          setConnectionStatus('Connected');
          setSseEventSource(eventSource);
        };

        eventSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log('SSE message received:', data);

            // Add to SSE slice
            dispatch(addSSEMessage(data));

            // If it's a message, add to message slice
            if (data.type === 'message' || data.messageType) {
              dispatch(addMessage(data));
              setHasNewMessages(true);

              // Clear new message indicator after 3 seconds
              setTimeout(() => setHasNewMessages(false), 3000);
            }

            // If it's a notification, refresh notification count
            if (data.type === 'notification') {
              dispatch(fetchUnreadNotificationCount(userId));
              setHasNewMessages(true);
              setTimeout(() => setHasNewMessages(false), 3000);
            }
          } catch (error) {
            console.error('Error parsing SSE message:', error);
          }
        };

        eventSource.onerror = (error) => {
          console.error('SSE connection error:', error);
          setConnectionStatus('Connection error');
          eventSource.close();

          // Attempt to reconnect after 5 seconds
          setTimeout(() => {
            if (userId) {
              console.log('Attempting SSE reconnection...');
              connectSSE();
            }
          }, 5000);
        };

        return eventSource;
      } catch (error) {
        console.error('Failed to create SSE connection:', error);
        setConnectionStatus('Failed to connect');
        return null;
      }
    };

    const eventSource = connectSSE();

    // Cleanup function
    return () => {
      if (eventSource) {
        console.log('Closing SSE connection');
        eventSource.close();
        setSseEventSource(null);
        setConnectionStatus('Disconnected');
      }
    };
  }, [userId, dispatch]);

  // Handle recent message updates
  useEffect(() => {
    if (recentMessage && Object.keys(recentMessage).length > 0) {
      setHasNewMessages(true);
      setTimeout(() => setHasNewMessages(false), 3000);
    }
  }, [recentMessage]);

  // Get message priority for sorting
  const getMessagePriority = (messageType) => {
    if (!messageType || !BUSINESS_MESSAGE_TYPES[messageType]) return 0;

    const priorities = {
      'CRITICAL': 4,
      'HIGH': 3,
      'MEDIUM': 2,
      'LOW': 1
    };

    return priorities[BUSINESS_MESSAGE_TYPES[messageType].priority] || 0;
  };

  // Get business message configuration
  const getBusinessMessageConfig = (messageType) => {
    return BUSINESS_MESSAGE_TYPES[messageType] || null;
  };

  // Check if user can send specific message type
  const canSendMessageType = (messageType) => {
    if (!messageType || !BUSINESS_MESSAGE_TYPES[messageType]) return true;

    const config = BUSINESS_MESSAGE_TYPES[messageType];
    return config.senderRoles.includes(effectiveUserRole.toUpperCase()) ||
      effectiveUserRole === 'admin';
  };

  // Check if user can receive specific message type
  const canReceiveMessageType = (messageType) => {
    if (!messageType || !BUSINESS_MESSAGE_TYPES[messageType]) return true;

    const config = BUSINESS_MESSAGE_TYPES[messageType];
    return config.targetRoles.includes('ALL') ||
      config.targetRoles.includes(effectiveUserRole.toUpperCase()) ||
      effectiveUserRole === 'admin';
  };

  // Combined unread count (messages + notifications)
  const combinedUnreadCount = unreadCounts.total + notificationUnreadCount;

  return {
    // Counts
    totalUnreadCount: unreadCounts.total,
    businessUnreadCount: unreadCounts.business,
    notificationUnreadCount,
    combinedUnreadCount,

    // Connection status
    isConnected,
    connectionStatus,
    sseEventSource,

    // Message state
    hasNewMessages,
    filteredMessages,
    businessMessageTypes,

    // Business logic
    getMessagePriority,
    getBusinessMessageConfig,
    canSendMessageType,
    canReceiveMessageType,

    // User context
    effectiveUserRole,

    // Loading state
    isLoading: loading.conversations || loading.messages
  };
};

export default useUniversalMessaging;
